// Device identity helpers for license-exclusivity enforcement.
// - A persistent fingerprint is stored in localStorage (survives app restarts).
// - Device type is inferred from the User-Agent.
// - GPS location is best-effort (browser permission); never blocks the flow.

const FP_KEY = 'rmp_device_fp';

const randomId = () => {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* noop */ }
  return 'fp-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export const getDeviceFingerprint = () => {
  let fp = localStorage.getItem(FP_KEY);
  if (!fp) {
    fp = randomId();
    localStorage.setItem(FP_KEY, fp);
  }
  return fp;
};

export const getDeviceType = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  const isMobile = /android|iphone|ipad|ipod|iemobile|blackberry|opera mini|mobile/i.test(ua);
  return isMobile ? 'mobile' : 'pc';
};

// Best-effort GPS location. Resolves to null on denial/timeout/unsupported.
export const getGeo = (timeoutMs = 6000) =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve(null);
    let done = false;
    const finish = (val) => { if (!done) { done = true; resolve(val); } };
    const timer = setTimeout(() => finish(null), timeoutMs + 500);
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timer);
          finish({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
        },
        () => { clearTimeout(timer); finish(null); },
        { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 600000 }
      );
    } catch (e) {
      clearTimeout(timer);
      finish(null);
    }
  });

// Gather the full device descriptor for the unlock request.
export const collectDeviceInfo = async () => {
  const geo = await getGeo();
  return {
    device_fingerprint: getDeviceFingerprint(),
    device_type: getDeviceType(),
    user_agent: navigator.userAgent || '',
    geo,
  };
};
